// Gate Intake prefill rules (F01 UF-07). Pure, so the "never silently change a
// value the operator touched" promise is testable without the 3 000-line page.
//
// 1. One PUC → filled. When the operator picks a grower (or Confirm Arrival
//    opens a WhatsApp draft with a grower and no PUC), the PUC step is ARMED
//    for that grower. Once that grower's PUC list has arrived, a PUC that is
//    still empty and has exactly one option is filled — the one-option rule
//    useGrowerFarmBlockFilter applies to the farm step. It stays visible and
//    changeable. The arm is spent either way, so clearing the PUC afterwards
//    sticks, and a seeded or hand-picked PUC is never replaced.
// 2. Last truck → OFFERED. The grower's last vehicle reg + driver become a
//    chip; nothing is written to the form without the operator's tap, and the
//    chip hides once the form already holds exactly that truck. A tap fills
//    the halves the last delivery recorded and leaves the others as typed.

/** A grower's earlier truck, as useGrowerLastTruck reads it. */
export interface LastTruck {
  vehicle_reg: string | null
  driver_name: string | null
}

/** The newest row (rows arrive newest-first) that recorded a real reg or
 *  driver. A reg/driver saved as '' or spaces passes PostgREST's not-null
 *  filter, so without this a blank latest delivery wins `limit(1)` and hides
 *  an older real truck (lastTruckOffer would then offer nothing). */
export function latestRealTruck(rows: readonly LastTruck[] | null | undefined): LastTruck | null {
  return (rows ?? []).find(t => !!(t.vehicle_reg?.trim() || t.driver_name?.trim())) ?? null
}

export interface PucAutoFill<T> {
  /** The option to fill, or null to leave the PUC alone. */
  fill: T | null
  /** True once the decision is made — the caller disarms. */
  settled: boolean
}

export function pucAutoFill<T>(
  armedFor: string | null,
  growerId: string,
  pucId: string,
  /** The grower's PUC list; undefined while it is still loading. */
  pucs: T[] | undefined,
): PucAutoFill<T> {
  if (!armedFor) return { fill: null, settled: false }
  // Armed for a grower that is no longer picked: spend it, fill nothing.
  if (armedFor !== growerId) return { fill: null, settled: true }
  if (pucs === undefined) return { fill: null, settled: false }
  return { fill: !pucId && pucs.length === 1 ? pucs[0] : null, settled: true }
}

export interface LastTruckOffer {
  vehicle_reg: string
  driver_name: string
  /** "Last time: CA 123-456 · T. Mokoena" */
  text: string
}

const norm = (s: string | null | undefined) => (s ?? '').trim().replace(/\s+/g, ' ').toUpperCase()

export function lastTruckOffer(
  last: LastTruck | null | undefined,
  form: { vehicle_reg: string; driver_name: string },
): LastTruckOffer | null {
  const reg = (last?.vehicle_reg ?? '').trim()
  const driver = (last?.driver_name ?? '').trim()
  if (!reg && !driver) return null
  // Already on the form — nothing to offer. A half the last delivery never
  // recorded is not offered, so it cannot keep the chip up either.
  const regOn = !reg || norm(reg) === norm(form.vehicle_reg)
  const driverOn = !driver || norm(driver) === norm(form.driver_name)
  if (regOn && driverOn) return null
  return { vehicle_reg: reg, driver_name: driver, text: `Last time: ${[reg, driver].filter(Boolean).join(' · ')}` }
}
